import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Phone, Mail, MapPin, MessageCircle, Clock, ArrowRight,
  Target, Heart, Headphones, Eye, Handshake,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import analytics from "@/utils/analytics";
import { whatsAppUrl } from "@/utils/whatsapp";

/**
 * Post-signup confirmation page.
 *
 * URL: /thank-you-for-signing-up — referenced from Zoho automations in the
 * wider business, so the path is load-bearing. React Router matches both
 * /thank-you-for-signing-up and /thank-you-for-signing-up/, preserving the
 * legacy WordPress trailing-slash form.
 *
 * Content mirrors the legacy WordPress page (candopayments.com/thank-you-for-
 * signing-up/) but rendered in the new site's design system. noindex,nofollow
 * because the page is only reachable from automation emails; sitemap excludes
 * it for the same reason.
 *
 * Note: legacy Gravity Form sidebar omitted — duplicates the /contact page
 * form; contact details are preserved as an inline contact card.
 */

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const gridStagger = { visible: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } } };

function FeatureCard({ icon: Icon, title, children }) {
  return (
    <motion.div
      variants={fadeUp}
      className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-7 shadow-sm h-full"
    >
      <div className="w-12 h-12 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-4">
        <Icon size={20} className="text-brand-aqua" />
      </div>
      <h3 className="font-heading text-lg font-bold text-brand-black mb-3">{title}</h3>
      <p className="text-sm text-brand-grey leading-relaxed">{children}</p>
    </motion.div>
  );
}

function ContactItem({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-brand-aqua" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-wider text-brand-grey mb-0.5">{label}</p>
        {children}
      </div>
    </div>
  );
}

export default function ThankYouSignupPage() {
  return (
    <div data-testid="thank-you-signup-page">
      <SEOHead
        title="Thank You for Signing Up - Welcome!"
        description="Thank You for Signing Up! We're excited to have you on board and look forward to supporting your journey."
        path="/thank-you-for-signing-up"
        robots="noindex, nofollow"
      />

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center max-w-3xl mx-auto">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">
              Welcome Aboard
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-5">
              <span className="gradient-text">Thank you</span> for signing up
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">
              We&rsquo;re excited to have you on board and look forward to supporting your journey.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Welcome to CanDo Payments */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-5">
              Welcome to CanDo Payments
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-brand-grey leading-relaxed">
              Thank you for choosing Elavon as your trusted payments partner. At CanDo Payments, we believe payments should support your business, not complicate it. We know how important it is to have systems you can rely on every day, with people you can speak to when something needs attention. That&rsquo;s why we combine the stability and proven capability of Elavon with the personal, relationship&#8209;driven service of a family&#8209;run team. You&rsquo;re not just signing up for payment processing &mdash; you&rsquo;re joining a partnership built around care, clarity and long&#8209;term support.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Why Elavon and CanDo Payments? */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center max-w-3xl mx-auto mb-10">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">
              Why Elavon and CanDo Payments?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-brand-grey leading-relaxed">
              Choosing to work with Elavon through CanDo Payments gives you access to world&#8209;class acquiring, backed by people who take the time to understand how your business actually works.
            </motion.p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={gridStagger}
            className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto"
          >
            <FeatureCard icon={Target} title="Tailored for you">
              Every business has its own rhythm. A caf&eacute; managing busy morning peaks, an online retailer handling weekend surges, a salon working with appointments, or an event&#8209;based business moving from site to site &mdash; each has specific challenges that a generic payment package won&rsquo;t solve. We look at your day&#8209;to&#8209;day workflow, your customer journey and your long&#8209;term plans. Only then do we recommend a mix of payment tools that genuinely fit. The aim is always practical: simple onboarding, smooth transactions and technology that helps rather than distracts.
            </FeatureCard>
            <FeatureCard icon={Heart} title="Family-driven care">
              CanDo Payments was founded by Sam and Gary Cameron. Their background in hospitality and running small businesses means they understand the pressure of customer service, staffing, operations and cashflow. They&rsquo;ve built the team around those same principles: treat people well, respond quickly, and keep things straightforward. Everyone here is hand&#8209;picked not just for technical skill, but for their ability to support customers calmly and capably. And then there&rsquo;s Bailey, the office dog, who keeps the team grounded and adds a bit of warmth to the working day. It&rsquo;s a reminder that business is ultimately about people.
            </FeatureCard>
          </motion.div>
        </div>
      </section>

      {/* Our Promise to You */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger} className="text-center max-w-3xl mx-auto mb-10">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">
              Our promise to you
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-brand-grey leading-relaxed">
              We want every interaction to feel easy. Payments are essential to running a business, but they shouldn&rsquo;t take your time or energy away from the work that matters.
            </motion.p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={gridStagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-5"
          >
            <FeatureCard icon={Headphones} title="Real people, every time">
              When you need help, you&rsquo;ll speak directly with a human being who knows your account and can resolve things without passing you between departments. No long automated menus. No generic ticketing queues. No repeating information. Just straightforward support.
            </FeatureCard>
            <FeatureCard icon={Eye} title="Transparent pricing">
              You&rsquo;ll have clear rates with no hidden extras. We don&rsquo;t bury fees in the small print or force long, inflexible terms. Contracts start at 12&ndash;18 months and move to a rolling monthly term. As your business grows or shifts direction, your payment setup can adapt with you.
            </FeatureCard>
            <FeatureCard icon={Handshake} title="A partnership that lasts">
              Our goal is to support you not just at the beginning, but throughout the life of your business. Whether you add new locations, expand online, introduce phone payments or simply want more efficient reporting, we&rsquo;re here to help you make the right decisions at the right time.
            </FeatureCard>
          </motion.div>
        </div>
      </section>

      {/* Need to contact us? */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">
              Need to contact us?
            </h2>
            <p className="text-base text-brand-grey leading-relaxed">
              We love hearing from you. Whether you need advice, help with setup, or want to explore additional solutions &mdash; like online ordering, merchant cash advance or virtual terminals &mdash; our friendly experts are just a message or call away.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <ContactItem icon={Phone} label="Call us free">
                <a href="tel:+448003682989" className="text-brand-black font-semibold hover:text-brand-aqua transition-colors" data-testid="thank-you-phone">
                  0800 368 2989
                </a>
              </ContactItem>
              <ContactItem icon={Mail} label="Email us">
                <a href="mailto:hello@CanDoPayments.com" className="text-brand-black font-semibold hover:text-brand-aqua transition-colors break-all" data-testid="thank-you-email">
                  hello@CanDoPayments.com
                </a>
              </ContactItem>
              <ContactItem icon={Clock} label="Office hours">
                <p className="text-brand-black font-semibold">Monday &mdash; Friday, 9am &mdash; 5pm</p>
              </ContactItem>
              <ContactItem icon={MapPin} label="Visit us">
                <address className="text-brand-black font-semibold not-italic">
                  The Technology Centre, Inward Way, Ellesmere Port, CH65 3EN
                </address>
              </ContactItem>
            </div>

            <div className="mt-8 pt-6 border-t border-brand-light-grey/50 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/book"
                className="inline-flex items-center justify-center gap-2 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
                data-testid="thank-you-book-cta"
              >
                Book a call <ArrowRight size={14} />
              </Link>
              <a
                href={whatsAppUrl("support")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.clickWhatsApp("thank_you_signup")}
                className="inline-flex items-center justify-center gap-2 bg-white border border-brand-light-grey hover:border-brand-aqua text-brand-black font-semibold rounded-full px-6 py-3 text-sm transition-colors"
                data-testid="thank-you-whatsapp-cta"
              >
                <MessageCircle size={14} /> Message us on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
